import React, { useState, useContext, useEffect } from 'react';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { CircularProgress, Tooltip } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import {
  CustomLabel,
  CustomSwitch,
} from 'components/Common/MUIStyledComponents';
interface IProfileContentProps {
  curUser: any;
  theme: any;
  toggleTheme: any;
  onResetPassword: () => void;
  onEnableMfa: () => void;
  onDeleteAccount: () => void;
  mfaEnabled?: boolean;
  isResettingPassword?: boolean;
  isTogglingMfa?: boolean;
}
import { IconTrash, IconUsers } from 'pages/credit-plans/SVGIcons';
import { Modal } from '@mui/material';
import DeleteTeamModal from 'components/TeamManagement/DeleteTeamModal';
import { GlobalContext } from 'providers/GlobalProvider';
import { licenseConfig, LicenseType } from 'pages/teams/LicenseBadge';
import { useTeamTrialStatus } from 'hooks/useTeamTrialStatus';
import LicenseTrialSegment from 'components/Common/LicenseTrialSegment';
import LicensesAPI from 'api/LicensesAPI';

const ProfileContent: React.FC<IProfileContentProps> = ({
  curUser,
  theme,
  toggleTheme,
  onResetPassword,
  onEnableMfa,
  mfaEnabled,
  isResettingPassword,
  isTogglingMfa,
}) => {
  const isMfaEnabled = Boolean(mfaEnabled);
  const [deleteMode, setDeleteMode] = useState<boolean>(false);
  const [adminsExpanded, setAdminsExpanded] = useState(false);
  const { cur_user } = useContext(GlobalContext);
  const userLicense = (curUser?.license || 'none').toLowerCase() as LicenseType;
  const normalizedLicense: LicenseType = licenseConfig[userLicense]
    ? userLicense
    : 'none';
  const trialStatus = useTeamTrialStatus(curUser);
  const hasTrialSegment =
    trialStatus.status === 'trialing' || trialStatus.status === 'expired';
  const licenseBadgeConfig = licenseConfig[normalizedLicense];
  const [leadershipMembers, setLeadershipMembers] = useState<any[]>([]);

  useEffect(() => {
    const teamId = cur_user?.selected_team?.id;
    const teamMembers: any = Array.isArray(cur_user?.selected_team?.members)
      ? cur_user.selected_team.members
      : [];

    if (!teamId) {
      setLeadershipMembers([]);
      return undefined;
    }

    let isCurrent = true;

    const memberById = new Map(
      teamMembers
        .filter((member: any) => member?.id)
        .map((member: any) => [String(member.id), member]),
    );

    const fetchLeadershipMembers = async () => {
      const res = await LicensesAPI.getLicensesForTeam(teamId);

      if (!isCurrent) {
        return;
      }

      if (!res?.success) {
        setLeadershipMembers([]);
        return;
      }

      const assignments = Array.isArray(res?.result) ? res.result : [];
      const leaders = assignments
        .filter((assignment: any) => {
          const role = String(assignment?.role ?? '').toLowerCase();
          return role === 'owner' || role === 'admin';
        })
        .map((assignment: any) => {
          const member: any =
            memberById.get(String(assignment?.memberId)) ?? {};
          const firstName = member?.firstName ?? assignment?.firstName ?? '';
          const lastName = member?.lastName ?? assignment?.lastName ?? '';
          const name =
            `${firstName} ${lastName}`.trim() ||
            member?.username ||
            assignment?.name ||
            assignment?.username ||
            member?.email ||
            assignment?.email ||
            '';

          return {
            id: String(assignment?.memberId ?? member?.id ?? name),
            name: name,
            email: member?.email ?? assignment?.email ?? '',
            role: String(assignment?.role ?? '').toLowerCase(),
          };
        });

      setLeadershipMembers(leaders);
    };

    fetchLeadershipMembers();

    return () => {
      isCurrent = false;
    };
  }, [cur_user?.selected_team?.id, cur_user?.selected_team?.members]);

  const owner = leadershipMembers.find((m) => m.role === 'owner');
  const otherAdmins = leadershipMembers.filter((m) => m.role === 'admin');
  const COLLAPSED_COUNT = 3;
  const visibleAdmins = adminsExpanded
    ? otherAdmins
    : otherAdmins.slice(0, COLLAPSED_COUNT);
  const hiddenCount = otherAdmins.length - COLLAPSED_COUNT;
  const initialsOf = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();

  return (
    <>
      <div className='profile-content'>
        <div className='profile-basics'>
          <div className='profile-section-card'>
            <div className='profile-card-inner'>
              <div className='profile-card-primary'>Full Name</div>
              <div className='profile-card-secondary'>
                {curUser?.basics?.firstName ? curUser?.basics?.firstName : ''}{' '}
                {curUser?.basics?.lastName ? curUser?.basics?.lastName : ''}
              </div>
            </div>
          </div>
          <div className='profile-section-card'>
            <div className='profile-card-inner'>
              <div className='profile-card-primary'>Email</div>
              <div className='profile-card-secondary'>
                {curUser?.basics?.email ? curUser?.basics?.email : ''}
              </div>
            </div>
          </div>
          <div className='profile-section-card'>
            <div className='profile-card-inner'>
              <div className='profile-card-primary'>License</div>
              <div className='profile-card-secondary'>
                <span
                  className={`${licenseBadgeConfig.badgeClass} profile-license-badge${
                    hasTrialSegment ? ' license-chip-inner-wrap' : ''
                  }`}
                >
                  <span className={licenseBadgeConfig.dotClass} />
                  {licenseBadgeConfig.label}
                  {hasTrialSegment && (
                    <LicenseTrialSegment
                      status={trialStatus.status as 'trialing' | 'expired'}
                    />
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Team Leadership */}
        <div className='pf-leadership-card'>
          <div className='pf-leadership-header'>
            <div className='pf-leadership-icon'>
              <IconUsers />
            </div>
            <div>
              <h3 className='pf-leadership-title'>Team Leadership</h3>
              <p className='pf-leadership-subtitle'>
                Reach out to your team owner or admins for access changes
              </p>
            </div>
          </div>
          <div className='pf-people-list'>
            {owner && (
              <div className='pf-person'>
                <div className='pf-avatar pf-avatar-owner'>
                  {initialsOf(owner.name)}
                </div>
                <div className='pf-person-body'>
                  <div className='pf-person-name'>{owner.name}</div>
                  <div className='pf-person-email'>{owner.email}</div>
                </div>
                <span className='pf-role-badge pf-role-owner'>Owner</span>
              </div>
            )}
            {visibleAdmins.map((a) => (
              <div className='pf-person' key={a.id}>
                <div className='pf-avatar'>{initialsOf(a.name)}</div>
                <div className='pf-person-body'>
                  <div className='pf-person-name'>{a.name}</div>
                  <div className='pf-person-email'>{a.email}</div>
                </div>
                <span className='pf-role-badge pf-role-admin'>Admin</span>
              </div>
            ))}
          </div>
          {otherAdmins.length > COLLAPSED_COUNT && (
            <button
              type='button'
              className='pf-leadership-toggle'
              onClick={() => setAdminsExpanded((v) => !v)}
              aria-expanded={adminsExpanded}
            >
              {adminsExpanded
                ? 'Show less'
                : `Show all admins (${hiddenCount} more)`}
              <span
                className={`pf-leadership-toggle-chevron${
                  adminsExpanded ? ' pf-leadership-toggle-chevron-open' : ''
                }`}
                aria-hidden='true'
              >
                <svg
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <polyline points='6 9 12 15 18 9' />
                </svg>
              </span>
            </button>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div
            className='api-container-left-h1'
            id='your-databases-section'
            style={{ paddingTop: '0px' }}
          >
            Preferences
          </div>
        </div>
        <div className='profile-basics'>
          <div className='profile-section-card'>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                flex: 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {theme === 'light' ? (
                  <Tooltip
                    title={'Dark Mode'}
                    placement='bottom-start'
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          display: 'flex',
                          padding:
                            'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          alignSelf: 'stretch',
                          borderRadius: 'var(--radius-md, 8px)',
                          background: 'var(--Background-200, #ffffff)',
                          color: '#333333',
                          maxWidth: '700px',
                          '& .MuiTooltip-arrow': {
                            color: 'var(--Background-200, #ffffff)',
                          },
                        },
                      },
                    }}
                  >
                    <DarkModeOutlinedIcon sx={{ color: '#1a1b1e' }} />
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={'Light Mode'}
                    placement='bottom-start'
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          display: 'flex',
                          padding:
                            'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          alignSelf: 'stretch',
                          borderRadius: 'var(--radius-md, 8px)',
                          background: 'var(--Background-200, var(--bg-colors))',
                          color: 'var(--text-prim25)',
                          maxWidth: '700px',
                          '& .MuiTooltip-arrow': {
                            color: 'var(--Background-200, #202227)',
                          },
                        },
                      },
                    }}
                  >
                    <LightModeOutlinedIcon />
                  </Tooltip>
                )}
              </div>
              <div className='profile-card-inner'>
                <div className='profile-card-primary'>Appearance</div>
                <div
                  className='profile-card-secondary'
                  style={{ textTransform: 'capitalize' }}
                >
                  {theme} mode
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CustomLabel
                  control={
                    <CustomSwitch
                      checked={theme === 'light' ? true : false}
                      onChange={toggleTheme}
                      name='ticket'
                    />
                  }
                />
              </div>
            </div>
          </div>

          <div className='profile-section-card'>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                flex: 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <ShieldOutlinedIcon sx={{ color: 'var(--table-text)' }} />
              </div>
              <div className='profile-card-inner'>
                <div className='profile-card-primary'>Multi-Factor Auth</div>
                <div className='profile-card-secondary'>
                  {isMfaEnabled ? 'Enabled' : 'Disabled'}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CustomLabel
                  control={
                    <CustomSwitch
                      checked={isMfaEnabled}
                      onChange={onEnableMfa}
                      disabled={Boolean(isTogglingMfa)}
                      name='ticket'
                    />
                  }
                />
                {isTogglingMfa ? (
                  <CircularProgress size={18} style={{ marginLeft: '6px' }} />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className='profile-basics'>
          <div className='profile-section-card'>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                flex: 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <KeyOutlinedIcon sx={{ color: 'var(--table-text)' }} />
              </div>
              <div className='profile-card-inner'>
                <div className='profile-card-primary'>Reset Password</div>
                <div className='profile-card-secondary'>
                  Send a password reset email
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <div
                  className='audit-modal-cont-footer-content-btn-cancel'
                  onClick={() => {
                    if (!isResettingPassword) onResetPassword();
                  }}
                >
                  <div className='audit-modal-cont-footer-content-btn-text'>
                    {isResettingPassword ? (
                      <CircularProgress
                        size={18}
                        style={{ marginRight: '8px' }}
                      />
                    ) : null}
                    {isResettingPassword ? 'Sending...' : 'Reset'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='profile-section-card'>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                flex: 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <DeleteForeverOutlinedIcon sx={{ color: '#f04438' }} />
              </div>
              <div className='profile-card-inner'>
                <div className='profile-card-primary'>Delete Account</div>
                <div className='profile-card-secondary'>
                  Clicking this button will send a request to info@bitedata.io.
                  Please allow at least 24 hours for processing.
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <button
                  className='tm-btn tm-btn-delete-team'
                  onClick={() => setDeleteMode(true)}
                >
                  <IconTrash /> Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ marginTop: '10px' }} className='additional-licenses-text'>
        Please contact us at info@bitedata.io to change the email associated
        with this account.
      </div>
      <Modal open={deleteMode} onClose={() => setDeleteMode(false)}>
        <DeleteTeamModal
          team={cur_user.selected_team}
          handleClose={() => setDeleteMode(false)}
          type='User'
          cur_user={curUser}
        />
      </Modal>
    </>
  );
};

export default ProfileContent;
